import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { PersonsV3 } from '@frontend/api-person';
import { SMSDataSharedUtils, SMSDataV4 } from '@frontend/api-sms-data';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { MiniChatHeaderProps, useMiniChatShallowStore } from '@frontend/mini-chat-store';
import { usePersonPhoneSelector } from '@frontend/person-phone-selector';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { ThreadDrawer } from '@frontend/thread-header';
import { MenuOption, ThreadOptionsDrawerState } from './thread-options-drawer';

type UseThreadOptionsDrawerArgs = Pick<ComponentProps<typeof ThreadDrawer>, 'open'> &
  Pick<MiniChatHeaderProps, 'groupId' | 'personPhone' | 'locationPhone'> & {
    associatedContacts: Person[];
    chatId: string;
    personId?: string;
  };

export const useThreadOptionsDrawer = ({
  chatId,
  groupId,
  personPhone,
  locationPhone,
  personId,
  associatedContacts,
  open,
}: UseThreadOptionsDrawerArgs): ThreadOptionsDrawerState => {
  const [selectedMenu, setSelectedMenu] = useState<MenuOption>();
  const { chats, setChatPossiblePersonPhones, setChatContextPersonId } = useMiniChatShallowStore(
    'chats',
    'setChatPossiblePersonPhones',
    'setChatContextPersonId'
  );

  const isUnifiedThreadEnabled = SMSDataSharedUtils.useIsUnifiedThreadEnabled([groupId]);

  const { updateThreadsWithNewPerson } = SMSDataV4._QueryUpdaters.useQueryUpdaters();
  const { updateUnifiedThreadsWithNewPerson } = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();
  const {
    data: { locationPhonesByLocation, allLocationPhones },
  } = InboxLocationPhones.Hooks.useLocationPhones();
  const defaultLocationPhone =
    locationPhonesByLocation[groupId]?.length === 1 ? locationPhonesByLocation[groupId][0]?.locationPhone : undefined;
  const currentPossiblePhones = chats.find((chat) => chat.chatId === chatId)?.possiblePersonPhones ?? [];

  useEffect(() => {
    if (!open) {
      setSelectedMenu(undefined);
    }
  }, [open]);

  const personPhoneSelectorState = usePersonPhoneSelector({
    personPhone,
    personId,
    groupId,
  });

  const locationPhoneSelectorState = useMemo<ThreadOptionsDrawerState['locationPhoneSelectorState']>(() => {
    return {
      selectedPersonPhone: { personPhone, personId },
      selectedLocationPhone: {
        locationPhone: formatPhoneNumberE164(locationPhone) || formatPhoneNumberE164(defaultLocationPhone ?? ''),
        locationId: groupId,
      },
      hideCurrentLocationPhone: true,
    };
  }, [defaultLocationPhone, groupId, locationPhone, personId, personPhone]);

  useEffect(() => {
    const newPossiblePhones = personPhoneSelectorState.allPhones.map(({ number }) => formatPhoneNumberE164(number));
    if (
      newPossiblePhones.length !== currentPossiblePhones.length ||
      !newPossiblePhones.some((newPhone) => !currentPossiblePhones.includes(newPhone)) ||
      !currentPossiblePhones.some((currentPhone) => !newPossiblePhones.includes(currentPhone))
    ) {
      setChatPossiblePersonPhones(chatId, newPossiblePhones);
    }
  }, [JSON.stringify(personPhoneSelectorState.allPhones)]);

  const hideOptions = useMemo<MenuOption[]>(() => {
    const result: MenuOption[] = [];
    if (!personPhoneSelectorState.showButton && allLocationPhones.length < 2) result.push('change-thread');
    if (associatedContacts.length <= 1) result.push('associated-contacts');
    return result;
  }, [allLocationPhones.length, associatedContacts.length, personPhoneSelectorState.showButton]);

  const { mutateAsync } = PersonsV3.PersonMutations.useUpsertPrimaryContactLegacyMutation();

  const setContextContactId = useCallback<ThreadOptionsDrawerState['setContextContactId']>(
    (contactId) => {
      setChatContextPersonId(contactId, chatId);
    },
    [chatId, setChatContextPersonId]
  );

  const setPrimaryContact = useCallback<ThreadOptionsDrawerState['setPrimaryContact']>(
    async ({ personId, firstName, preferredName, lastName }) => {
      setContextContactId(personId);
      await mutateAsync(
        {
          personId,
          phoneNumber: personPhone,
          locationId: groupId,
        },
        {
          onSuccess: () => {
            if (isUnifiedThreadEnabled) {
              updateUnifiedThreadsWithNewPerson({
                personPhones: [personPhone],
                personId,
                firstName,
                lastName,
                preferredName,
                onlyEmptyPersons: false,
                locationId: groupId,
              });
            } else {
              updateThreadsWithNewPerson({
                personPhones: [personPhone],
                personId,
                firstName,
                lastName,
                preferredName,
                onlyEmptyPersons: false,
                locationId: groupId,
              });
            }
          },
        }
      );
    },
    [
      groupId,
      mutateAsync,
      personPhone,
      setContextContactId,
      updateThreadsWithNewPerson,
      updateUnifiedThreadsWithNewPerson,
      isUnifiedThreadEnabled,
    ]
  );

  return {
    selectedMenu,
    setSelectedMenu,
    setContextContactId,
    setPrimaryContact,
    hideOptions,
    personPhoneSelectorState,
    locationPhoneSelectorState,
    associatedContacts,
  };
};
